import {
  EFormFieldOptionsSourceKind,
  type IActionDefinitionDto,
  type ICRUDFieldOptionsItem,
  type ICRUDFieldOptionsSource
} from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import { DataKeyTextFieldList } from "../../right-panel/properties-panel/data-key-editor/data-key-text-field-list";
import {
  actionRequiresParameterBinding,
  listOptionColumns,
  listValueColumns,
  newActionSource,
  newFixedListSource,
  optionActionLabel,
  usableString,
  type IOptionColumn
} from "./forms-tab-options-source";

/**
 * Where ONE SelectBox / MultiSelect field gets its options from.
 *
 * The row itself carries a single compact trigger; everything that needs room lives here. Only
 * two kinds exist in this platform — a hand-entered fixed list, or a list-returning action — and
 * an unbound field renders an EMPTY dropdown, which is the symptom this editor removes.
 *
 * The fixed-list half is NOT re-implemented: `DataKeyTextFieldList` is the platform's own
 * key/text editor (drag-to-reorder, duplicate-key check, JSON-string contract) and it brings its
 * own CANCEL / UPDATE footer, so on that branch the modal deliberately renders no footer of its
 * own. The action half needs one, so it has one.
 */

const TAB_FIXED: string = "Fixed list";
const TAB_ACTION: string = "Action";

export interface IFormsTabOptionsSourceModalProps {
  isOpen: boolean;
  /** Label of the field being configured — shown so the modal says WHICH dropdown this is. */
  fieldName: string;
  /**
   * The field's data type. Gates the value column: what that column returns is written into the
   * process parameter this field is bound to, so an incompatible column is never offered.
   */
  fieldTypeName: string | undefined | null;
  /**
   * The field's current declaration. Admits `null` because that is what the server sends for an
   * unset source — the type saying only `undefined` is how this crashed twice.
   */
  value: ICRUDFieldOptionsSource | null | undefined;
  /** Actions the user may pick — already filtered by the caller. */
  actions: IActionDefinitionDto[];
  onClose: () => void;
  /** A source to store, or `undefined` to clear the declaration. */
  onSave: (source: ICRUDFieldOptionsSource | undefined) => void;
}

const FormsTabOptionsSourceModal = ({
  isOpen,
  fieldName,
  fieldTypeName,
  value,
  actions,
  onClose,
  onSave
}: IFormsTabOptionsSourceModalProps) => {
  const [tab, setTab] = useState<string>(TAB_FIXED);
  const [actionDefinitionId, setActionDefinitionId] = useState<string | undefined>(undefined);
  const [fieldToDisplay, setFieldToDisplay] = useState<string | undefined>(undefined);
  const [fieldToUseAsKey, setFieldToUseAsKey] = useState<string | undefined>(undefined);

  // Re-seed from the stored declaration every time the modal opens, so a previous aborted
  // session never leaks into the next field's editor.
  useEffect((): void => {
    if (!isOpen) return;
    const isAction: boolean = value?.kind === EFormFieldOptionsSourceKind.Action;
    setTab(isAction ? TAB_ACTION : TAB_FIXED);
    // `usableString`, not `??`: the server sends an unset member as JSON `null`, and an empty
    // string is not a column name either — both mean "nothing chosen".
    setActionDefinitionId(isAction ? usableString(value?.actionDefinitionId) : undefined);
    setFieldToDisplay(isAction ? usableString(value?.fieldToDisplay) : undefined);
    setFieldToUseAsKey(isAction ? usableString(value?.fieldToUseAsKey) : undefined);
  }, [isOpen, value]);

  const selectedAction: IActionDefinitionDto | undefined = actions.find(
    (action: IActionDefinitionDto): boolean => action.id === actionDefinitionId
  );
  const displayColumns: IOptionColumn[] = listOptionColumns(selectedAction);
  const valueColumns: IOptionColumn[] = listValueColumns(selectedAction, fieldTypeName);

  /** Picking an action pre-selects its columns the way the platform guesses them elsewhere. */
  const handleActionChange = (next: string | number | (string | number)[] | null): void => {
    const nextId: string | undefined = typeof next === "string" && next.length > 0 ? next : undefined;
    setActionDefinitionId(nextId);
    const action: IActionDefinitionDto | undefined = actions.find(
      (candidate: IActionDefinitionDto): boolean => candidate.id === nextId
    );
    if (action === undefined) {
      setFieldToDisplay(undefined);
      setFieldToUseAsKey(undefined);
      return;
    }
    const guessed: ICRUDFieldOptionsSource = newActionSource(action, fieldTypeName);
    setFieldToDisplay(usableString(guessed.fieldToDisplay));
    setFieldToUseAsKey(usableString(guessed.fieldToUseAsKey));
  };

  /** The fixed-list editor hands back its JSON-string contract; store it as typed items. */
  const handleFixedListChange = (json: string): void => {
    let items: ICRUDFieldOptionsItem[] = [];
    try {
      const parsed: unknown = JSON.parse(json);
      if (Array.isArray(parsed)) {
        items = parsed as ICRUDFieldOptionsItem[];
      }
    } catch {
      // The editor only emits what it serialized itself, so this is unreachable in practice —
      // but a parse failure must not take the wizard down with it.
      items = [];
    }
    onSave(newFixedListSource(items));
  };

  const handleSaveAction = (): void => {
    if (selectedAction === undefined) return;
    onSave({
      kind: EFormFieldOptionsSourceKind.Action,
      actionDefinitionId: selectedAction.id,
      actionName: selectedAction.name,
      fieldToDisplay,
      fieldToUseAsKey
    });
  };

  const storedFixedJson: string =
    value?.kind === EFormFieldOptionsSourceKind.FixedList ? JSON.stringify(value.fixedItems ?? []) : "[]";

  const renderActionBranch = () => {
    if (actions.length === 0) {
      return (
        <div className="kuika-wf-forms__optsrc-empty" data-testid="processFormsOptionsSourceNoActions">
          <KuikaSvg name="info-circle" width={16} height={16} color="var(--blue-6)" />
          <span>
            No usable action in this project yet. An action can feed a dropdown when it returns a LIST and needs no
            parameters — one that requires a parameter cannot run on its own here, so it would show an empty list.
          </span>
        </div>
      );
    }

    return (
      <div className="kuika-wf-forms__optsrc-action">
        <label className="kuika-wf-forms__optsrc-field">
          <span className="kuika-wf-forms__optsrc-label">Action</span>
          <KuikaSelectbox
            size="md"
            searchable
            allowClear
            placeholder="Select an action"
            value={actionDefinitionId ?? null}
            onChange={handleActionChange}
            onClear={(): void => handleActionChange(null)}
            options={actions.map((action: IActionDefinitionDto) => ({
              value: action.id,
              label: optionActionLabel(action),
              searchLabel: optionActionLabel(action)
            }))}
          />
        </label>

        <label className="kuika-wf-forms__optsrc-field">
          <span className="kuika-wf-forms__optsrc-label">Text shown to the user</span>
          <KuikaSelectbox
            size="md"
            searchable
            placeholder={selectedAction === undefined ? "Select an action first" : "Select a column"}
            disabled={selectedAction === undefined}
            value={fieldToDisplay ?? null}
            onChange={(next: string | number | (string | number)[] | null): void =>
              setFieldToDisplay(typeof next === "string" ? next : undefined)
            }
            options={displayColumns.map((column: IOptionColumn) => ({
              value: column.name,
              label: `${column.name} (${column.typeName})`,
              searchLabel: column.name
            }))}
          />
        </label>

        <label className="kuika-wf-forms__optsrc-field">
          <span className="kuika-wf-forms__optsrc-label">Value stored in the parameter</span>
          <KuikaSelectbox
            size="md"
            searchable
            placeholder={selectedAction === undefined ? "Select an action first" : "Select a column"}
            disabled={selectedAction === undefined}
            value={fieldToUseAsKey ?? null}
            onChange={(next: string | number | (string | number)[] | null): void =>
              setFieldToUseAsKey(typeof next === "string" ? next : undefined)
            }
            options={valueColumns.map((column: IOptionColumn) => ({
              value: column.name,
              label: `${column.name} (${column.typeName})`,
              searchLabel: column.name
            }))}
          />
          {selectedAction !== undefined && actionRequiresParameterBinding(selectedAction) && (
            <span className="kuika-wf-forms__optsrc-hint" data-testid="processFormsOptionsSourceNeedsParams">
              This action takes arguments. It runs on the screen&apos;s page load, so select the screen in the component
              list and bind its parameters under <strong>ACTIONS</strong> — until then the dropdown stays empty.
            </span>
          )}
          {selectedAction !== undefined && valueColumns.length < displayColumns.length && (
            <span className="kuika-wf-forms__optsrc-hint" data-testid="processFormsOptionsSourceTypeHint">
              Only columns whose type fits this field
              {fieldTypeName ? ` (${fieldTypeName})` : ""} are listed — a value the parameter cannot hold would arrive
              empty.
            </span>
          )}
        </label>
      </div>
    );
  };

  const isActionTab: boolean = tab === TAB_ACTION;

  return (
    <KModal
      showModal={isOpen}
      closeModal={onClose}
      width={720}
      header={`Options for “${fieldName}”`}
      // On the fixed-list branch the embedded editor owns the footer (its own UPDATE / CANCEL),
      // so adding a second one here would give the user two competing save buttons.
      footer={
        isActionTab ? (
          <KModalFooter
            onDelete={value == null ? undefined : (): void => onSave(undefined)}
            deleteLabel="Remove source"
            onCancel={onClose}
            submitLabel="Save"
            onSubmit={handleSaveAction}
            disabled={selectedAction === undefined}
          />
        ) : undefined
      }
    >
      <div className="kuika-wf-forms__optsrc" data-testid="processFormsOptionsSourceModal">
        <p className="kuika-wf-forms__optsrc-intro">
          This field renders a dropdown. Until its options are bound it shows nothing, so pick where they come from.
        </p>

        <KuikaTabs
          className="kuika-wf-forms__optsrc-tabs"
          size="lg"
          value={tab}
          options={[
            { value: TAB_FIXED, label: "Fixed list" },
            { value: TAB_ACTION, label: "From an action" }
          ]}
          onChange={(next: string): void => setTab(next)}
          dataTestId="processFormsOptionsSourceTabs"
        />

        {isActionTab ? (
          renderActionBranch()
        ) : (
          <DataKeyTextFieldList
            value={storedFixedJson}
            onChange={handleFixedListChange}
            handleClickOnCancel={onClose}
          />
        )}
      </div>
    </KModal>
  );
};

export default FormsTabOptionsSourceModal;
